import { useEffect, useSyncExternalStore } from "react";

/**
 * Телефон — не «узкий десктоп», а другой способ ввода.
 *
 * Разделение идёт по ШИРИНЕ, а не по `pointer: coarse`: планшет с тачем спокойно
 * держит трёхколоночную раскладку, а узкое окно на десктопе должно вести себя как
 * телефон — иначе отладка мобильного вида требует телефона. Порог тот же, что у
 * `md` в Tailwind, чтобы CSS-варианты `max-md:` и этот хук не разъезжались.
 */
const PHONE_QUERY = "(max-width: 767px)";

function subscribe(onChange: () => void): () => void {
	const list = window.matchMedia(PHONE_QUERY);
	list.addEventListener("change", onChange);
	return () => list.removeEventListener("change", onChange);
}

export function usePhone(): boolean {
	return useSyncExternalStore(
		subscribe,
		() => window.matchMedia(PHONE_QUERY).matches,
		() => false
	);
}

/**
 * Высота экранной клавиатуры в `--kb-inset`.
 *
 * На iOS клавиатура НЕ уменьшает layout viewport: `position: fixed; bottom: 0`
 * уезжает под неё, и панель действий — единственное, чем на телефоне вообще
 * можно сделать отступ или новую строку, — оказывается недоступна ровно тогда,
 * когда нужна. Считаем разницу между layout- и visual-viewport и держим её в
 * переменной: CSS дальше сам решает, кому это важно.
 *
 * Вызывать один раз, в оболочке приложения.
 */
export function useKeyboardInset(): void {
	useEffect(() => {
		const viewport = window.visualViewport;
		if (!viewport) {
			return;
		}
		const apply = () => {
			const inset = Math.max(0, window.innerHeight - (viewport.height + viewport.offsetTop));
			// Округляем: дробные пиксели дают дрожание панели на инерционном скролле.
			document.documentElement.style.setProperty("--kb-inset", `${Math.round(inset)}px`);
		};
		apply();
		viewport.addEventListener("resize", apply);
		viewport.addEventListener("scroll", apply);
		return () => {
			viewport.removeEventListener("resize", apply);
			viewport.removeEventListener("scroll", apply);
		};
	}, []);
}

/**
 * Полоса состояния под текущую тему.
 *
 * iOS красит ею и панель Safari, и — если приложение открыто с домашнего экрана —
 * место под вырезом. Значение берётся из `<meta name="theme-color">`, а оно в разметке
 * одно и захардкожено тёмным: переключив тему на светлую, человек получал белую
 * страницу с чёрной шапкой.
 *
 * Цвет НЕ дублируется константой, а снимается с живого фона: иначе он разъедется с
 * палитрой при первой же правке токенов. Прогон через канвас нужен потому, что фон
 * задан в `oklch`, а `theme-color` разбирает не CSS-движок страницы — плоский rgb
 * принимают все.
 *
 * Вызывать после каждой смены темы.
 */
export function syncStatusBarColor(): void {
	const meta = document.querySelector('meta[name="theme-color"]');
	const context = document.createElement("canvas").getContext("2d");
	if (!meta || !context) {
		return;
	}
	context.fillStyle = getComputedStyle(document.body).backgroundColor;
	context.fillRect(0, 0, 1, 1);
	const [r, g, b] = context.getImageData(0, 0, 1, 1).data;
	meta.setAttribute("content", `rgb(${r} ${g} ${b})`);
}

/**
 * Нажать клавишу за человека.
 *
 * Кнопки «отступ» и «новая строка» обязаны делать РОВНО то же, что `Tab` и `Enter`
 * в редакторе: там висит нетривиальный порядок (дослать правку до переезда строки,
 * запомнить каретку, перевести редактор на новый ключ). Дублировать эту цепочку
 * второй раз — значит завести второе место, где она разъедется. Поэтому кнопка
 * не повторяет обработчик, а отправляет в него настоящее событие.
 *
 * React слушает на корне, так что событие с `bubbles` доходит до тех же
 * `onKeyDown`, что и живое нажатие.
 */
export function pressKey(key: string, modifiers: { shift?: boolean; alt?: boolean; meta?: boolean } = {}): void {
	const target = document.activeElement ?? document.body;
	target.dispatchEvent(
		new KeyboardEvent("keydown", {
			key,
			bubbles: true,
			cancelable: true,
			shiftKey: modifiers.shift ?? false,
			altKey: modifiers.alt ?? false,
			metaKey: modifiers.meta ?? false
		})
	);
}
